import { Node, mergeAttributes } from '@tiptap/core';
import { getStyleHTMLAttributes, type StyleAttrs } from '../utils/styleHelper';

export interface StatsOptions {
  HTMLAttributes: Record<string, unknown>;
}

/**
 * Creates a style attribute configuration for Stats extensions.
 * Parses inline styles from HTML and converts them to a style object.
 */
function createStyleAttribute() {
  return {
    default: null,
    parseHTML: (element: HTMLElement) => {
      const styleString = element.getAttribute('style');
      if (!styleString) return null;

      const styleObj: Record<string, string> = {};
      styleString.split(';').forEach((rule) => {
        const [property, value] = rule.split(':').map((s) => s.trim());
        if (property && value) {
          // Convert kebab-case to camelCase
          const camelProperty = property.replace(/-([a-z])/g, (_, letter: string) =>
            letter.toUpperCase()
          );
          styleObj[camelProperty] = value;
        }
      });
      return Object.keys(styleObj).length > 0 ? styleObj : null;
    },
    renderHTML: () => ({}), // Style is rendered separately
  };
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    statsGrid: {
      insertStatsGrid: (columns?: number) => ReturnType;
    };
    statItem: {
      insertStatItem: () => ReturnType;
    };
  }
}

// Stats grid container
export const StatsGridExtension = Node.create<StatsOptions>({
  name: 'statsGrid',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  group: 'block',

  content: 'statItem+',

  defining: true,

  isolating: true,

  addAttributes() {
    return {
      columns: {
        default: 3,
        parseHTML: (element) => {
          const cols = element.dataset.columns;
          if (cols) return parseInt(cols, 10);
          // Try to infer from class
          const classList = element.className || '';
          const match = classList.match(/grid-cols-(\d+)/);
          return match ? parseInt(match[1], 10) : 3;
        },
        renderHTML: (attributes) => ({
          'data-columns': attributes.columns,
        }),
      },
      style: createStyleAttribute(),
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div.stats-grid',
      },
      {
        tag: 'div.stats-container',
      },
      {
        tag: 'div[data-stats-grid]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const columns = HTMLAttributes['data-columns'] || 3;
    const styleAttrs = getStyleHTMLAttributes(node.attrs as StyleAttrs);

    return [
      'div',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, styleAttrs, {
        class: `stats-grid grid grid-cols-${columns} gap-4`,
        'data-stats-grid': '',
      }),
      0,
    ];
  },

  addCommands() {
    return {
      insertStatsGrid:
        (columns = 3) =>
        ({ commands }) => {
          const items = [];
          for (let i = 0; i < columns; i++) {
            items.push({
              type: 'statItem',
              attrs: {
                value: '0',
                label: 'Statistic',
              },
            });
          }
          return commands.insertContent({
            type: this.name,
            attrs: { columns },
            content: items,
          });
        },
    };
  },
});

// Individual stat item
export const StatItemExtension = Node.create<StatsOptions>({
  name: 'statItem',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  group: 'block',

  content: 'inline*',

  defining: true,

  addAttributes() {
    return {
      value: {
        default: '0',
        parseHTML: (element) => {
          const valueEl = element.querySelector('.stat-value, [data-stat-value]');
          return valueEl?.textContent?.trim() || element.dataset.statValue || '0';
        },
        renderHTML: (attributes) => ({
          'data-stat-value': attributes.value,
        }),
      },
      label: {
        default: '',
        parseHTML: (element) => {
          const labelEl = element.querySelector('.stat-label, [data-stat-label]');
          return labelEl?.textContent?.trim() || element.dataset.statLabel || '';
        },
        renderHTML: (attributes) => ({
          'data-stat-label': attributes.label,
        }),
      },
      prefix: {
        default: '',
        parseHTML: (element) => element.dataset.statPrefix || '',
        renderHTML: (attributes) =>
          attributes.prefix ? { 'data-stat-prefix': attributes.prefix } : {},
      },
      suffix: {
        default: '',
        parseHTML: (element) => element.dataset.statSuffix || '',
        renderHTML: (attributes) =>
          attributes.suffix ? { 'data-stat-suffix': attributes.suffix } : {},
      },
      style: createStyleAttribute(),
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div.stat-item',
      },
      {
        tag: 'div[data-stat-item]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const value = HTMLAttributes['data-stat-value'] || '0';
    const label = HTMLAttributes['data-stat-label'] || '';
    const prefix = HTMLAttributes['data-stat-prefix'] || '';
    const suffix = HTMLAttributes['data-stat-suffix'] || '';
    const styleAttrs = getStyleHTMLAttributes(node.attrs as StyleAttrs);

    return [
      'div',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, styleAttrs, {
        class: 'stat-item text-center p-4 bg-gray-50 rounded-lg',
        'data-stat-item': '',
      }),
      [
        'div',
        { class: 'stat-value text-3xl font-bold text-primary' },
        `${prefix}${value}${suffix}`,
      ],
      ['div', { class: 'stat-label text-sm text-gray-600 mt-1' }, label],
    ];
  },

  addCommands() {
    return {
      insertStatItem:
        () =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              value: '0',
              label: 'New Stat',
            },
          });
        },
    };
  },
});

export default StatsGridExtension;
